import { useState, createContext, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from './UserProvider';
import { APICode, DynamicPriceType, ExcursionPickupType, ForGroup, OrderStatus, PaymentMethod, PriceType, SellerType } from '../models/types';
import useAPI from '../hooks/useAPI';
import API from '../server/api';
import Logger from '../utils/logger';
import Message from '../models/Message';
import Order from '../models/order/Order';
import { RepositoryContext } from './RepositoryProvider';
import { ALERT_TYPE } from '../components/Alert';
import User from '../models/user/User';
import { Step } from '../components/Stepper2';
import OrderSession from '../models/session/OrderSession';
import { STORAGE } from '../defs/storage';
import { getStorageValue, setStorageValue } from '../hooks/useLocalStorage';
import log from '../utils/logger';

export const OrderContext = createContext();

export const STEPS = {
  common_info: 0,
  client_info: 1,
  payment_booking: 2,
  print: 3
};

const initialState = {
  steps: [],
  order: null,
  session: null,
  schedule: null,
  excursion: null,
  payment: {},
  common_errors: [],
  client_errors: [],
  payment_errors: [],
};

const initSteps = (t) => {
  return Object.keys(STEPS).map((step) => new Step({ title: t(`order:steps.${step}`), id: ~~(Math.random() * 10000) }));
};

export const OrderProvider = (props) => {
  const logger = new Logger({ name: 'OrderProvider', showDate: false, showFile: false });
  const {t} = useTranslation(['common', 'order']);

  const [page, setPage] = useState(0);
  const [orderInfo, setOrderInfo] = useState(initialState);
  const [orderSession, setOrderSession] = useState(getStorageValue(STORAGE.ORDER_SESSION));
  const [prices, setPrices] = useState([]);
  const [steps, setSteps] = useState(initSteps(t));

  const { callApi } = useAPI();

  const userInfo = useContext(UserContext);
  const repository = useContext(RepositoryContext);

  const nextPage = () => {
    let newPage = page + 1;

    if (newPage <= steps.length && newPage >= 0) {
      steps[page].complete();

      if (newPage < steps.length) {
        steps[newPage].select();
        setPage(newPage);
      }
    }
  };

  const prevPage = () => {
    let newPage = page - 1;

    if (newPage < steps.length && newPage >= 0) {
      steps[page].reset();
      steps[newPage].select();
      setPage(newPage);
    }
  };

  const selectPage = (idx, allComplete) => {
    if (idx < steps.length && idx >= 0) {
      if (allComplete) {
        for (let p = 0; p <= idx; ++p) {
          steps[p].complete();
        }
      }

      setPage(idx);
    }
  };

  const reset = () => {
    setSteps(initSteps(t));
    setPage(0);
    setOrderSession(new OrderSession());
  };

  const _setSession = (field, value) => {
    setOrderSession((prevState) => ({ ...prevState, [field]: value }));

    if (orderInfo.session === null || orderInfo.session[field] !== value) {
      let session = new OrderSession({ ...getStorageValue(STORAGE.ORDER_SESSION), [field]: value });
      setStorageValue(STORAGE.ORDER_SESSION, session);
      _setStore('session', session);
    }
  };

  const _useSession = (...args) => {
    const [field] = args;

    useEffect(() => {
      if (args.length > 1) {
        _setSession(field, args[1]);
      } else {
        _setSession(field, orderSession[field]);
      }
    }, []);

    return [orderSession[field], (value) => _setSession(field, value)];
  };


  const _setStore = (field, value) => {
    setOrderInfo((prevState) => ({ ...prevState, [field]: value }));
  };

  const _useStore = (...args) => {
    const [field] = args;

    useEffect(() => {
      if (args.length > 1) {
        _setStore(field, args[1]);
      } else {
        _setStore(field, orderInfo[field]);
      }
    }, []);

    return [orderInfo[field], (value) => _setStore(field, value)];
  };


  const getOrder = () => {
    return orderInfo.order;
  };

  const getOrderInfo = () => {
    return orderInfo;
  };


  const getPaymentAmount = () => {
    let amount = 0;
    let discountAmount = 0;
    let discountCount = 0;

    if (prices.length > 0) {
      amount = orderSession.passengers.reduce((amount, passenger) => {
        let price = prices.find((price) => price.id === passenger.excursion_price) || { amount: 0 };
        let expectedAmount = price.amount;


        if (orderSession.discount) {
          //let needCount = false;

           let needCount = orderSession.discount_forGroups === ForGroup.ALL && !price.price.noSeat;
           needCount = needCount || (orderSession.discount_forGroups === ForGroup.ADULTS && price.price.priceType === PriceType.NORMAL && price.price.noSeat);
           needCount = needCount || (orderSession.discount_forGroups === ForGroup.CHILDREN && price.price.priceType === PriceType.CHILD && price.price.noSeat);

          if (needCount) {
            if (orderSession.discount_type !== DynamicPriceType.ABSOLUTE) {
              discountAmount += (price.amount * (orderSession.discount_value / 100).toFixed(2)) * passenger.count;
            }
            discountCount += passenger.count;
          }
          expectedAmount = price.amount;
        } else {
          expectedAmount = price.partialPaymentAllowed ? price.partialValue : price.amount;
        }

        return amount + passenger.count * expectedAmount;
      }, 0);
    }

    if (orderSession.discount_type === DynamicPriceType.ABSOLUTE && discountCount > 0) {
      amount -= discountCount * orderSession.discount_value;
    } else if (discountAmount > 0) {
      amount -= discountCount * Math.floor((discountAmount / discountCount) * 100) / 100;
    }

    return orderInfo.order?.paymentDetails?.partialValue > 0 ? orderInfo.order?.paymentDetails?.partialValue : (orderInfo.order?.paymentDetails?.amount ?? amount);
  };


  const getSeatNumbers = () => {
    let seats = [];

    if (orderInfo.order) {
      seats = orderInfo.order.seats.map((seat) => seat.userSeats).reduce((seats, seat) => seats.concat(seat), []);
    } else if (orderInfo.schedule) {
      return ['-'];
      /*
      let placeNumber = orderInfo.schedule.stat.totalSeats + 1;
      seats = orderSession.passengers.reduce((seats, passenger) => {
        let price = prices.find((price) => price.id === passenger.excursion_price) || { amount: 0 };

        if (!price.noSeat) {
          seats.push(placeNumber++);
        }

        return seats;
      }, []);
      */
    }

    return seats;
  };


  const getSchedule = () => {
    return new Promise(async (resolve, reject) => {
      if (orderInfo.schedule) {
        resolve(orderInfo.schedule);
      } else if (orderSession.schedule) {
        repository.request(API.schedule.schedule, { id: orderSession.schedule }).then((schedule) => {
          resolve(schedule);
        });
      } else {
        resolve(null);
      }
    });
  };

  const getTotalAmount = () => {
    let amount = orderSession.passengers.reduce((amount, passenger) => {
      let price = prices.find((price) => price.id === passenger.excursion_price) || { amount: 0 };
      return amount + passenger.count * price.amount;
    }, 0);
    return amount;
  };


  const addError = (field, code, title, message) => {
    _addError(field, new Message({ code, title, message, type: ALERT_TYPE.ERROR }));
    _setStore(field, orderInfo[field]);
  };

  const clearErrors = (type) => {
    if (!type || type === 'common_errors') {
      _setStore('common_errors', []);
    }
    if (!type || type === 'client_errors') {
      _setStore('client_errors', []);
    }
    if (!type || type === 'payment_errors') {
      _setStore('payment_errors', []);
    }
  };

  const _addError = (type, message) => {
    if (!orderInfo[type].find((e) => e.code === message.code)) {
      orderInfo[type].push(message);
    }
  };

  const removeError = (type, code) => {
    let errorIdx = orderInfo[type].findIndex((e) => e.code === code);
    if (errorIdx > -1) {
      orderInfo[type].splice(errorIdx, 1);
    }
    _setStore(type, orderInfo[type]);
  };

  const hasError = (type, code) => {
    let errorIdx = orderInfo[type].findIndex((e) => e.code === code);
    return errorIdx > -1;
  };

  const validate = async (step) => {
    switch(step) {
      case STEPS.common_info: {
        orderInfo.common_errors = [];

        if (!orderSession.excursion) {
          _addError('common_errors', new Message({ code: 'excursion_not_set', title: t('order:common_info.error.excursion_not_set'), type: ALERT_TYPE.ERROR }));
        } else {
          let excursion = (await repository.getModel('Excursion', orderSession.excursion)).value;

          if (excursion && excursion.passportRequired &&
            orderSession.passport_required && !(orderSession.passengers.reduce((count, passenger) => count + passenger.passports.reduce((count, passport) => count + (passport && passport.fullName && passport.number ? 1 : 0), 0), 0) === orderSession.common_passengers.reduce((count, passenger) => count + passenger.count, 0))
          ) {
            _addError('common_errors', new Message({ code: 'passports_not_set', title: t('order:common_info.error.passports_not_set'), type: ALERT_TYPE.ERROR }));
          }
        }
        if (!orderSession.passengers.length) {
          _addError('common_errors', new Message({ code: 'passengers_not_set', title: t('order:common_info.error.passengers_not_set'), type: ALERT_TYPE.ERROR }));
        }
        if (!orderSession.date) {
          _addError('common_errors', new Message({ code: 'date_not_set', title: t('order:common_info.error.date_not_set'), type: ALERT_TYPE.ERROR }));
        }
        if (!orderSession.time) {
          _addError('common_errors', new Message({ code: 'time_not_set', title: t('order:common_info.error.time_not_set'), type: ALERT_TYPE.ERROR }));
        }
        if (!orderSession.place && !orderSession.address) {
          _addError('common_errors', new Message({ code: 'place_not_set', title: t('order:common_info.error.place_not_set'), type: ALERT_TYPE.ERROR }));
        }
        if (orderSession.schedule) {
          let schedule = (await repository.getModel('Schedule', orderSession.schedule)).value;
          if (schedule && (schedule.limit.stopSelling && orderSession.passengers.reduce((count, passenger) => count + passenger.count, 0) > (schedule.limit.value - schedule.limit.cntSeats))) {
            _addError('common_errors', new Message({ code: 'passengers_over_limit', title: t('order:common_info.error.passengers_over_limit'), type: ALERT_TYPE.ERROR }));
          }
        }

        _setStore('common_errors', orderInfo.common_errors);
        return orderInfo.common_errors;
      }
      case STEPS.client_info: {
        orderInfo.client_errors = [];

        if (!orderSession.user && !(orderSession.phone && orderSession.first_name && orderSession.last_name)) {
          _addError('client_errors', new Message({ code: 'client_not_set', title: t('order:client_info.error.client_not_set'), type: ALERT_TYPE.ERROR }));
        }

        _setStore('client_errors', orderInfo.client_errors);
        return orderInfo.client_errors;
      }
      case STEPS.payment_booking: {
        orderInfo.payment_errors = [];
        _setStore('payment_errors', orderInfo.payment_errors);
        return orderInfo.payment_errors;
      }
      default: {}
    }
    return [];
  };


  const createOrder = () => {
    return new Promise(async (resolve, reject) => {
      let subdivision = userInfo.getSession().subdivision;
      let passports = [];

      orderSession.passengers.forEach((passenger) => {
        Object.values(passenger.passports).forEach((passport) => {
          passports.push({ number: passport.number, fullName: passport.fullName });
        });
      });

      if (subdivision) {
        const orderParams = {
          user: orderSession.user,
          excursion: orderSession.excursion,
          subdivision: orderSession.subdivision,
          schedule: orderSession.schedule,
          passengers: orderSession.passengers.map((p) => ({
            _id: p.excursion_price,
            price: p.price,
            count: p.count,
            ages: p.ages ?? []
          })),
          sellerType: SellerType.CASHIER,
          pointId: userInfo.getSession().point,
          paymentMethod: orderSession.payment_method || PaymentMethod.ONLINE,
          contactPhones: [ ...orderSession.contact_phones ],
          wishes: orderSession.wishes,
          place: orderInfo.excursion && orderInfo.excursion.pickupType === ExcursionPickupType.PLACE ? orderSession.place : undefined,
          address: orderInfo.excursion && orderInfo.excursion.pickupType === ExcursionPickupType.HOME ? orderSession.address : undefined,
          passports: passports,
          comment: orderSession.comment,
          discountId: orderSession.discount,
        };

        if (!orderSession.user) {
          orderParams.userInfo = {
            phone: orderSession.phone,
            name: `${orderSession.last_name} ${orderSession.first_name} ${orderSession.middle_name}`.trim()
          };
        }

        repository.request(API.order.createOrder, orderParams, Order).then((order) => {
          _setStore('order', order);
          if (order) {
            _setSession('order', order.id);
            _setSession('number', order.number);
            resolve(order);
          } else {
            reject()
          }
        }).catch((e) => {
          if (e.fields) {
            for (let field in e.fields) {
              _addError('client_errors', new Message({ code: e.error.toLowerCase(), title: t(`order:common_info.error.${field}_${e.fields[field]}`), type: ALERT_TYPE.ERROR }));
            }
          }
          _setStore('client_errors', orderInfo.client_errors);
          logger.error('New Order error:', e);
          reject(e);
        });
      } else {
        logger.error('New Order subdivision is null');
        reject({ error: 'subdivision_is_null' });
      }
    });
  };

  const cancelOrder = () => {
    return new Promise(async (resolve, reject) => {
      if (orderInfo.order?.id) {
        repository.request(API.order.status, { orderId: orderInfo.order.id, status: OrderStatus.CANCELLED }).then((order) => {
          resolve(order);
        }).catch((e) => {
          reject(e);
          log.error(e);
        });
      } else {
        resolve(null);
      }
    });
  };

  const isNewPhone = async () => {
    if (orderSession.phone) {
      let response = await callApi(API.auth.checkPhone, { phone: orderSession.phone });
      return response.data === APICode.USER_NOT_FOUND;
    }
    return false;
  };

  const askUser = async (accountId) => {
    if (accountId) {
      let response = await callApi(API.user.user, { id: accountId });
      if (response.error) {
        return response;
      } else {
        let user = new User(response.data);
        return user;
      }
    }
    return false;
  };


  const getSession = () => {
    return orderInfo.session || getStorageValue(STORAGE.ORDER_SESSION, orderSession, OrderSession);
  };

  const clearSession = () => {
    setOrderSession({...(new OrderSession())});
  };


  useEffect(() => {
    const orderSession = getStorageValue(STORAGE.ORDER_SESSION, null, OrderSession);

    for (let field in orderSession) {
      _setSession(field, orderSession[field]);
    }
  }, []);


  return (
    <OrderContext.Provider
      value={{
        page,
        steps,
        nextPage,
        prevPage,
        reset,
        setPage: selectPage,

        useSession: _useSession,
        useState: _useStore,

        getOrder,
        getOrderInfo,

        getSession,
        clearSession,

        addError,
        removeError,
        hasError,
        clearErrors,

        getPaymentAmount,
        getTotalAmount,
        getSeatNumbers,
        getSchedule,

        validate,
        setPrices,

        createOrder,
        cancelOrder,
        isNewPhone,
        askUser
      }}
    >
      {props.children}
    </OrderContext.Provider>
  );
};
